import { useSetRecoilState } from 'recoil'
import { useEffect } from 'react'
import recoilStateUpdaters from '../../../recoil/recoil.stateUpdaters'
import dashboardsAtom, { IDashboardsAtomState } from '../dashboards.atom'
import { produce } from 'immer'
import { IWidget } from '../dashboards.types'
import eventBus from '@/services/eventBus'
import dashboardGroupsAtom from '@/pages/Dashboards/dashboard.groups.atom.ts'

const useDashboardsEventsSubscriptions = () => {
  const setDashboardsState = useSetRecoilState(dashboardsAtom)
  const setDashboardGroupsState = useSetRecoilState(dashboardGroupsAtom)

  useEffect(() => {
    const createSub = eventBus.sub('dashboards.created', (dashboard) => {
      recoilStateUpdaters.itemCreated(setDashboardsState, dashboard)
    })

    const updateSub = eventBus.sub('dashboards.updated', (dashboard) => {
      recoilStateUpdaters.itemUpdated(setDashboardsState, dashboard)
    })

    const removeSub = eventBus.sub('dashboards.deleted', (id) => {
      recoilStateUpdaters.itemDeleted(setDashboardsState, id)
    })

    const createGroupSub = eventBus.sub('dashboards.groups.created', (dashboard) => {
      recoilStateUpdaters.itemCreated(setDashboardGroupsState, dashboard)
    })

    const updateGroupSub = eventBus.sub('dashboards.groups.updated', (dashboard) => {
      recoilStateUpdaters.itemUpdated(setDashboardGroupsState, dashboard)
    })

    const removeGroupSub = eventBus.sub('dashboards.groups.deleted', (id) => {
      recoilStateUpdaters.itemDeleted(setDashboardGroupsState, id)
    })

    const widgetCreateSub = eventBus.sub('dashboards.widgets.created', (widget) => {
      setDashboardsState((prev) =>
        produce(prev, (draft: IDashboardsAtomState) => {
          const dashboard = draft.items.find((d) => d.id === widget.dashboardId)
          if (!dashboard) {
            console.warn('widgets.created: dashboard not found')
            return draft
          }

          if (!dashboard.widgets.some((w) => w.id === widget.id)) {
            dashboard.widgets.push(widget)
          }

          return draft
        })
      )
    })

    const widgetUpdatedSub = eventBus.sub('dashboards.widgets.updated', (widget) => {
      setDashboardsState((prev) =>
        produce(prev, (draft: IDashboardsAtomState) => {
          const dashboard = draft.items.find((d) => d.id === widget.dashboardId)
          if (!dashboard) return

          const widgetIndex = dashboard.widgets.findIndex((w) => w.id === widget.id)
          if (widgetIndex === -1) return draft

          dashboard.widgets.splice(widgetIndex, 1, widget)
          return draft
        })
      )
    })

    const widgetDeletedSub = eventBus.sub('dashboards.widgets.deleted', (id: IWidget['id']) => {
      setDashboardsState((prev) =>
        produce(prev, (draft: IDashboardsAtomState) => {
          const dashboard = draft.items.find((d) => d.widgets.some((w) => w.id === id))
          if (!dashboard) return

          const widgetIndex = dashboard.widgets.findIndex((w) => w.id === id)
          if (widgetIndex === -1) return draft

          dashboard.widgets.splice(widgetIndex, 1)
          return draft
        })
      )
    })

    return () => {
      createSub()
      updateSub()
      removeSub()
      createGroupSub()
      updateGroupSub()
      removeGroupSub()
      widgetCreateSub()
      widgetUpdatedSub()
      widgetDeletedSub()
    }
  }, [])
}

export default useDashboardsEventsSubscriptions
